/**
 * StudentLiveEndScreen — le tuteur a clôturé le live (`live-end`, et `end-session-student` pour qui
 * la pousse encore) : `<StudentLiveEndPage>`.
 *
 * L'ancien écran (`EndSessionScreenStudent`) : le rapport du jour, demandé en arrivant
 * (`today-live-report`) ; « Ton activité a été mise en pause » quand une activité était ouverte
 * (`activity.ongoingActivity`) ; « Quitter le live » pose toujours la note par-dessus
 * (`<LiveRatingModal>`), comme l'ancien écran : la règle des quinze minutes ne vaut qu'au départ en
 * cours de live, et la séance clôturée n'est plus la prochaine occurrence, qui ne dirait pas son
 * heure. La note part avec ses chips, puis l'élève sort.
 *
 * L'écran du live reste monté dessous, comme l'ancien (`navigate`, pas `replace`) : la salle, son
 * relevé (qui ramène au live si le tuteur rouvre la séance) et la sortie au démontage sont les siens ;
 * la salle se quitte par la sortie, après la note. Le retour d'Android (bouton ou geste) n'y ramène
 * donc pas : il fait ce que fait « Quitter le live », la note puis la sortie — sans lui, il dépilait
 * la fin et rouvrait le live déjà fini, d'où l'élève sortait sans note.
 * La barre d'état suit le thème, comme sur le live (`ParentStatusBar`).
 *
 * Le rapport survit à la sortie du live (`live-end`) : il mène lui-même à l'accueil, une fois le live
 * quitté. L'élève qui sortait pendant la clôture (`left`) y arrive sa note donnée ou déclinée : il
 * n'a plus qu'à attendre que sa sortie aboutisse.
 */
import { useNavigation } from "@react-navigation/native"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { BackHandler } from "react-native"
import { useDispatch, useSelector } from "react-redux"

import { liveService } from "../../../../../../data/services/liveService"
import { StudentLiveEndPage } from "../../../../../../design-system/student"
import { useLiveLeave } from "../../../../../../engine/live/student/useLiveLeave"
import { apiCall } from "../../../../../../engine/shared/apiCall"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import { liveEndModel } from "../../../../view-models/student/Live/studentLiveEndViewModel"
import { ParentStatusBar } from "../../parent/ParentStatusBar"
import { type StudentSpaceScreenProps } from "../studentSpaceRouteTypes"
import { LIVE_EVENTS, trackLive } from "./liveTracking"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type RootState = {
    liveMode?: { liveSessionId?: number | null; isLiveModeEnabled?: boolean }
    activity?: { ongoingActivity?: boolean }
    data?: {
        staticData?: { school_subjects?: ApiSchoolSubject[] }
        currentUser?: {
            object?: {
                live_sessions?: {
                    id?: number
                    tutor?: { display_name?: string | null } | null
                }[]
            } | null
        }
    }
}

type Navigation = { popTo: (route: string) => void }

/** Ce que le live passe à `live-end` ; `end-session-student`, poussée par l'ancien code, n'a rien. */
type EndParams = { liveSessionId?: number | string; activityPaused?: boolean | string; left?: boolean | string }

// Au web, un paramètre relu de l'adresse est une chaîne.
const flag = (value: unknown) => value === true || value === "true"

export function StudentLiveEndScreen({ route }: StudentSpaceScreenProps) {
    const params: EndParams = route?.params ?? {}
    const navigation = useNavigation() as unknown as Navigation
    const dispatch = useDispatch()
    const telemetry = useMemo(() => createTelemetry("live-student.end", { role: "student" }), [])
    // Passées par le live : sa sortie, que le rapport survit, les efface du magasin.
    const storeSessionId = useSelector((state: RootState) => state.liveMode?.liveSessionId ?? null)
    const storePaused = useSelector((state: RootState) => !!state.activity?.ongoingActivity)
    const liveSessionId = params.liveSessionId != null ? Number(params.liveSessionId) : storeSessionId
    const activityPaused = params.activityPaused != null ? flag(params.activityPaused) : storePaused
    const left = flag(params.left)
    const inLive = useSelector((state: RootState) => state.liveMode?.isLiveModeEnabled === true)
    const subjects = useSelector((state: RootState) => state.data?.staticData?.school_subjects)
    const session = useSelector((state: RootState) =>
        selectDisplayedStudent(state)?.live_sessions?.find((candidate) => candidate?.id === liveSessionId)
    )
    const leave = useLiveLeave(liveSessionId, telemetry)
    const [raw, setRaw] = useState<unknown>(null)
    const [loading, setLoading] = useState(liveSessionId != null)

    useEffect(() => {
        if (liveSessionId == null) return
        apiCall(dispatch as never, liveService.getTodayLiveReport, { routeParams: { id: liveSessionId } })
            .then(setRaw)
            .catch((cause: unknown) => {
                telemetry.error("today_report", cause)
            })
            .finally(() => setLoading(false))
        // À l'arrivée, comme aujourd'hui.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveSessionId])

    const model = useMemo(() => liveEndModel(raw, subjects), [raw, subjects])
    // Il rentre une fois le live quitté : par sa note ici, ou par sa sortie déjà partie du live.
    const [goingHome, setGoingHome] = useState(false)
    useEffect(() => {
        if (goingHome && !inLive) navigation.popTo("menu")
    }, [goingHome, inLive, navigation])
    const quit = () => {
        if (goingHome || leave.leaving) return
        setGoingHome(true)
        if (inLive && !left) leave.openRating()
    }
    const onLeave = () => {
        trackLive(LIVE_EVENTS.endExit)
        quit()
    }

    // La note ouverte a son propre retour (sa feuille le prend d'abord) ; partie, plus rien à faire.
    const quitRef = useRef(quit)
    quitRef.current = quit
    useEffect(() => {
        const subscription = BackHandler.addEventListener("hardwareBackPress", () => {
            quitRef.current()
            return true
        })
        return () => subscription.remove()
    }, [])
    const tutorFirstname = (session?.tutor?.display_name ?? "").trim().split(/\s+/)[0] || "ton masteur"

    return (
        <>
            <StudentLiveEndPage
                activityPaused={activityPaused}
                report={model.report}
                reportLoading={loading}
                scores={model.scores}
                validatedActivities={model.validatedActivities}
                todoActivities={model.todoActivities}
                heroMessage={model.heroMessage}
                chapterSections={model.chapterSections}
                conceptGroups={model.conceptGroups}
                onLeave={onLeave}
                leaving={leave.leaving || goingHome}
                rating={{
                    visible: leave.rating.open,
                    tutorFirstname,
                    rating: leave.rating.rating,
                    onRate: leave.rate,
                    subtitle: leave.rating.subtitle,
                    comments: leave.rating.comments,
                    selectedComments: leave.rating.selected,
                    onToggleComment: leave.toggleComment,
                    onDismiss: () => {
                        trackLive(LIVE_EVENTS.ratingClosed)
                        leave.dismissRating()
                    },
                    onSubmit: leave.submitRating,
                    submitting: leave.rating.submitting,
                }}
            />
            <ParentStatusBar />
        </>
    )
}
